import React, { useState, useEffect } from 'react';
import { collection, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { habitaciones } from '../data/habitaciones';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Area, AreaChart
} from 'recharts';
import './AdminDashboard.css';
import FloatingMenu from './FloatingMenu';

const AdminStatistics = () => {
  // Estados para filtros
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(null); // null significa todos los meses
  
  // Estados para datos
  const [ingresosMensuales, setIngresosMensuales] = useState([]);
  const [ingresosPorHabitacion, setIngresosPorHabitacion] = useState([]);
  const [ocupacionPorHabitacion, setOcupacionPorHabitacion] = useState([]);
  const [personasPorMes, setPersonasPorMes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Generar años para el filtro (del actual hacia atrás 5 años)
  const availableYears = Array.from({ length: 6 }, (_, index) => new Date().getFullYear() - index);
  
  // Colores para los gráficos
  const COLORS = [
    '#8884d8', '#83a6ed', '#8dd1e1', '#82ca9d', '#a4de6c',
    '#d0ed57', '#ffc658', '#ff8042', '#ff6361', '#bc5090'
  ];

  // Array de nombres de meses
  const meses = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  // Cargar datos al inicio y cuando cambian los filtros
  useEffect(() => {
    cargarDatos();
  }, [selectedYear, selectedMonth]);

  // Función para cargar todos los datos
  const cargarDatos = async () => {
    try {
      setLoading(true);
      setError(null);

      // Obtener todas las reservas
      const reservasCollection = collection(db, 'reservas');
      const reservasSnapshot = await getDocs(reservasCollection);
      const todasReservas = reservasSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Filtrar para excluir las reservas bloqueadas
      const reservasValidas = todasReservas.filter(reserva => {
        const status = reserva.status;
        const nombre = reserva.nombre || reserva.name || '';
        return status !== 'blocked' && nombre !== 'Bloqueado';
      });

      // Filtrar por año y mes seleccionados
      const reservasFiltradas = reservasValidas.filter(reserva => {
        // Obtener fecha de entrada
        let fechaEntrada;
        if (reserva.fechaEntrada instanceof Timestamp) {
          fechaEntrada = reserva.fechaEntrada.toDate();
        } else if (typeof reserva.fechaEntrada === 'object' && reserva.fechaEntrada.seconds) {
          fechaEntrada = new Date(reserva.fechaEntrada.seconds * 1000);
        } else if (reserva.fechaEntrada instanceof Date) {
          fechaEntrada = reserva.fechaEntrada;
        } else {
          fechaEntrada = new Date(reserva.fechaEntrada || reserva.checkIn);
        }

        // Filtrar por año
        const matchesYear = fechaEntrada.getFullYear() === selectedYear;
        
        // Si no hay mes seleccionado (todos) o coincide con el mes seleccionado
        const matchesMonth = selectedMonth === null || fechaEntrada.getMonth() === selectedMonth;
        
        return matchesYear && matchesMonth;
      });

      console.log(`Procesando ${reservasFiltradas.length} reservas válidas para año ${selectedYear} ${
        selectedMonth !== null ? `y mes ${meses[selectedMonth]}` : 'y todos los meses'
      }`);
      
      // Procesar datos para gráficos
      procesarIngresosMensuales(reservasFiltradas);
      procesarIngresosPorHabitacion(reservasFiltradas);
      calcularOcupacionMensual(reservasFiltradas);
      calcularPersonasPorMes(reservasFiltradas);

      setLoading(false);
    } catch (err) {
      console.error("Error al cargar datos:", err);
      setError("Error al cargar datos. Por favor, intente nuevamente.");
      setLoading(false);
    }
  };

  // Procesar ingresos mensuales para el gráfico
  const procesarIngresosMensuales = (reservas) => {
    // Inicializar objeto para almacenar ingresos por mes
    const ingresosPorMes = {};
    
    // Inicializar todos los meses con cero
    for (let i = 0; i < 12; i++) {
      ingresosPorMes[i] = 0;
    }

    // Calcular ingresos por mes del año seleccionado
    reservas.forEach(reserva => {
      // Obtener fecha de entrada
      let fechaEntrada;
      if (reserva.fechaEntrada instanceof Timestamp) {
        fechaEntrada = reserva.fechaEntrada.toDate();
      } else if (typeof reserva.fechaEntrada === 'object' && reserva.fechaEntrada.seconds) {
        fechaEntrada = new Date(reserva.fechaEntrada.seconds * 1000);
      } else if (reserva.fechaEntrada instanceof Date) {
        fechaEntrada = reserva.fechaEntrada;
      } else {
        fechaEntrada = new Date(reserva.fechaEntrada || reserva.checkIn);
      }

      // Sumar al mes correspondiente
      const mes = fechaEntrada.getMonth();
      const precioTotal = reserva.precioTotal || reserva.totalPrice || 0;
      
      ingresosPorMes[mes] += parseInt(precioTotal);
    });

    // Convertir a formato para recharts
    const datosParaGrafico = Object.keys(ingresosPorMes).map(mes => ({
      name: meses[mes],
      ingresos: ingresosPorMes[mes]
    }));

    setIngresosMensuales(datosParaGrafico);
  };

  // Procesar ingresos por habitación para el gráfico
  const procesarIngresosPorHabitacion = (reservas) => {
    // Inicializar objeto para almacenar ingresos por habitación
    const ingresosPorHab = {};
    
    // Inicializar todas las habitaciones
    habitaciones.forEach(hab => {
      ingresosPorHab[hab.id] = {
        nombre: hab.nombre,
        ingresos: 0
      };
    });

    // Calcular ingresos por habitación
    reservas.forEach(reserva => {
      const habitacionId = reserva.habitacionId || reserva.roomId;
      
      if (habitacionId && ingresosPorHab[habitacionId]) {
        const precioTotal = reserva.precioTotal || reserva.totalPrice || 0;
        ingresosPorHab[habitacionId].ingresos += parseInt(precioTotal);
      }
    });

    // Convertir a formato para recharts
    const datosParaGrafico = Object.keys(ingresosPorHab)
      .filter(id => ingresosPorHab[id].ingresos > 0) // Solo incluir habitaciones con ingresos
      .map(id => ({
        name: `Hab. ${id}`,
        ingresos: ingresosPorHab[id].ingresos,
        nombreCompleto: ingresosPorHab[id].nombre
      }));

    setIngresosPorHabitacion(datosParaGrafico);
  };

  // Calcular la ocupación mensual por habitación
  const calcularOcupacionMensual = (reservas) => {
    // Si hay un mes seleccionado, calcular días en ese mes, sino usar promedio
    const diasEnMes = selectedMonth !== null 
      ? new Date(selectedYear, selectedMonth + 1, 0).getDate()
      : 30; // Promedio aproximado
    
    // Inicializar objeto para almacenar días ocupados por habitación
    const ocupacionHabitaciones = {};
    
    // Inicializar todas las habitaciones
    habitaciones.forEach(hab => {
      ocupacionHabitaciones[hab.id] = {
        id: hab.id,
        nombre: hab.nombre,
        diasOcupados: 0,
        ocupacion: 0
      };
    });
    
    // Analizar cada reserva
    reservas.forEach(reserva => {
      try {
        // Extraer fechas considerando diferentes estructuras de datos
        let fechaEntrada, fechaSalida;
        
        // Intentar obtener fechas de diferentes propiedades
        if (reserva.fechaEntrada) {
          fechaEntrada = obtenerFechaDesdeReserva(reserva.fechaEntrada);
        } else if (reserva.checkIn) {
          fechaEntrada = obtenerFechaDesdeReserva(reserva.checkIn);
        } else if (reserva.datosComunes && reserva.datosComunes.fechaEntrada) {
          fechaEntrada = obtenerFechaDesdeReserva(reserva.datosComunes.fechaEntrada);
        }
        
        if (reserva.fechaSalida) {
          fechaSalida = obtenerFechaDesdeReserva(reserva.fechaSalida);
        } else if (reserva.checkOut) {
          fechaSalida = obtenerFechaDesdeReserva(reserva.checkOut);
        } else if (reserva.datosComunes && reserva.datosComunes.fechaSalida) {
          fechaSalida = obtenerFechaDesdeReserva(reserva.datosComunes.fechaSalida);
        }
        
        // Si no hay fechas válidas, omitir esta reserva
        if (!fechaEntrada || !fechaSalida) {
          console.warn(`Reserva ${reserva.id} sin fechas válidas`);
          return;
        }
        
        // Normalizar a inicio del día
        fechaEntrada.setHours(0, 0, 0, 0);
        fechaSalida.setHours(0, 0, 0, 0);
        
        // Verificar período relevante para el filtro
        let inicioMes, finMes;
        
        if (selectedMonth !== null) {
          // Si hay mes seleccionado, usar ese mes específico
          inicioMes = new Date(selectedYear, selectedMonth, 1);
          finMes = new Date(selectedYear, selectedMonth + 1, 0);
        } else {
          // Si no hay mes seleccionado, usar todo el año
          inicioMes = new Date(selectedYear, 0, 1);
          finMes = new Date(selectedYear, 11, 31);
        }
        
        // Si la reserva no intersecta con el período, omitirla
        if (fechaSalida < inicioMes || fechaEntrada > finMes) {
          return;
        }
        
        // Calcular intervalo de ocupación dentro del período
        const inicioOcupacion = fechaEntrada < inicioMes ? inicioMes : fechaEntrada;
        const finOcupacion = fechaSalida > finMes ? finMes : fechaSalida;
        
        // Calcular días ocupados (hasta fecha de salida - 1 ya que el check-out no se cuenta como día ocupado)
        const diasOcupados = Math.max(0, Math.floor((finOcupacion - inicioOcupacion) / (1000 * 60 * 60 * 24)));
        
        // Obtener habitaciones de esta reserva
        let habitacionesReserva = [];
        
        if (reserva.habitaciones && Array.isArray(reserva.habitaciones)) {
          // Formato: reserva.habitaciones[].habitacionId
          habitacionesReserva = reserva.habitaciones.map(h => h.habitacionId || h.id).filter(Boolean);
        } else if (reserva.habitacionId) {
          // Formato: reserva.habitacionId
          habitacionesReserva = [reserva.habitacionId];
        } else if (reserva.roomId) {
          // Formato: reserva.roomId
          habitacionesReserva = [reserva.roomId];
        }
        
        // Actualizar días ocupados para cada habitación en esta reserva
        habitacionesReserva.forEach(habId => {
          const habitacionId = habId.toString();
          if (ocupacionHabitaciones[habitacionId]) {
            ocupacionHabitaciones[habitacionId].diasOcupados += diasOcupados;
          }
        });
      } catch (err) {
        console.warn(`Error al procesar reserva ${reserva.id}:`, err);
      }
    });
    
    // Calcular porcentaje de ocupación para cada habitación
    Object.values(ocupacionHabitaciones).forEach(hab => {
      hab.ocupacion = Math.round((hab.diasOcupados / diasEnMes) * 100);
    });
    
    // Convertir a array y ordenar por ID
    const datosOcupacion = Object.values(ocupacionHabitaciones)
      .filter(hab => hab.id) // Filtrar habitaciones sin ID
      .sort((a, b) => a.id - b.id);
    
    setOcupacionPorHabitacion(datosOcupacion);
  };

  // Calcular personas por mes
  const calcularPersonasPorMes = (reservas) => {
    // Inicializar objeto para almacenar número de personas por mes
    const personasPorMes = {};
    
    // Inicializar todos los meses con cero
    for (let i = 0; i < 12; i++) {
      personasPorMes[i] = 0;
    }

    // Calcular número de personas por mes
    reservas.forEach(reserva => {
      // Obtener fecha de entrada
      let fechaEntrada;
      if (reserva.fechaEntrada instanceof Timestamp) {
        fechaEntrada = reserva.fechaEntrada.toDate();
      } else if (typeof reserva.fechaEntrada === 'object' && reserva.fechaEntrada.seconds) {
        fechaEntrada = new Date(reserva.fechaEntrada.seconds * 1000);
      } else if (reserva.fechaEntrada instanceof Date) {
        fechaEntrada = reserva.fechaEntrada;
      } else {
        fechaEntrada = new Date(reserva.fechaEntrada || reserva.checkIn);
      }

      // Sumar personas al mes correspondiente
      const mes = fechaEntrada.getMonth();
      
      // Obtener número de personas
      let numPersonas = 0;
      
      // Caso 1: Reserva con múltiples habitaciones
      if (reserva.habitaciones && Array.isArray(reserva.habitaciones)) {
        // Sumar personas de todas las habitaciones
        numPersonas = reserva.habitaciones.reduce((sum, hab) => {
          return sum + (hab.personasEnHabitacion || hab.guests || 0);
        }, 0);
      } 
      // Caso 2: Reserva con una habitación
      else {
        numPersonas = reserva.personasEnHabitacion || reserva.guests || 1;
      }
      
      personasPorMes[mes] += parseInt(numPersonas);
    });

    // Convertir a formato para recharts
    const datosParaGrafico = Object.keys(personasPorMes).map(mes => ({
      name: meses[mes],
      personas: personasPorMes[mes]
    }));

    setPersonasPorMes(datosParaGrafico);
  };
  
  // Función auxiliar para obtener una fecha a partir de diferentes formatos
  const obtenerFechaDesdeReserva = (fechaInput) => {
    if (!fechaInput) return null;
    
    try {
      if (fechaInput instanceof Timestamp) {
        return fechaInput.toDate();
      } else if (typeof fechaInput === 'object' && fechaInput.seconds) {
        return new Date(fechaInput.seconds * 1000);
      } else if (fechaInput instanceof Date) {
        return fechaInput;
      } else {
        return new Date(fechaInput);
      }
    } catch (err) {
      console.warn("Error al convertir fecha:", err);
      return null;
    }
  };

  // Formatear moneda
  const formatearMoneda = (valor) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(valor);
  };

  // Personalizar tooltip del gráfico
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="grafico-tooltip">
          <p className="tooltip-label">{label}</p>
          <p className="tooltip-valor">Ingresos: {formatearMoneda(payload[0].value)}</p>
          {payload[0].payload.nombreCompleto && (
            <p className="tooltip-nombre">{payload[0].payload.nombreCompleto}</p>
          )}
        </div>
      );
    }
    return null;
  };

  // Personalizar tooltip del gráfico de personas
  const CustomTooltipPersonas = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="grafico-tooltip">
          <p className="tooltip-label">{label}</p>
          <p className="tooltip-valor">Personas: {payload[0].value}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="admin-dashboard">
      <h1>Estadísticas</h1>

      {/* Filtros */}
      <div className="statistics-filters">
        <div className="filter-container">
          <label htmlFor="yearFilter">Año:</label>
          <select 
            id="yearFilter" 
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          >
            {availableYears.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>

        <div className="filter-container">
          <label htmlFor="monthFilter">Mes:</label>
          <select 
            id="monthFilter" 
            value={selectedMonth === null ? '' : selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value === '' ? null : parseInt(e.target.value))}
          >
            <option value="">Todos los meses</option>
            {meses.map((mes, index) => (
              <option key={index} value={index}>{mes}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Mensaje de carga/error */}
      {loading && <div className="message loading">Cargando datos...</div>}
      {error && <div className="message error">{error}</div>}

      {!loading && !error && (
        <div className="dashboard-container">
          {/* Gráfico ingresos mensuales */}
          <div className="dashboard-card grafico-ingresos">
            <div className="card-header">
              <h2>Ingresos {selectedMonth !== null ? `- ${meses[selectedMonth]}` : 'Mensuales'} {selectedYear}</h2>
            </div>
            <div className="card-content">
              {ingresosMensuales.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={selectedMonth !== null ? [ingresosMensuales[selectedMonth]] : ingresosMensuales}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis tickFormatter={(value) => `$${(value/1000)}k`} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="ingresos" name="Ingresos" fill="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="mensaje-sin-datos">No hay datos de ingresos para mostrar</div>
              )}
            </div>
          </div>

          {/* Gráfico de personas por mes */}
          <div className="dashboard-card grafico-personas">
            <div className="card-header">
              <h2>Número de Huéspedes {selectedMonth !== null ? `- ${meses[selectedMonth]}` : 'por Mes'} {selectedYear}</h2>
            </div>
            <div className="card-content">
              {personasPorMes.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={selectedMonth !== null ? [personasPorMes[selectedMonth]] : personasPorMes}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis 
                      allowDecimals={false}
                      label={{ 
                        value: 'Número de Huéspedes', 
                        angle: -90, 
                        position: 'insideLeft' 
                      }}
                    />
                    <Tooltip content={<CustomTooltipPersonas />} />
                    <Legend />
                    <Bar dataKey="personas" name="Huéspedes" fill="#ffc658" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="mensaje-sin-datos">No hay datos de huéspedes para mostrar</div>
              )}
            </div>
          </div>

          {/* Gráfico ingresos por habitación */}
          <div className="dashboard-card grafico-habitaciones">
            <div className="card-header">
              <h2>Ingresos por Habitación {selectedMonth !== null ? `- ${meses[selectedMonth]}` : ''} {selectedYear}</h2>
            </div>
            <div className="card-content">
              {ingresosPorHabitacion.length > 0 ? (
                <div className="grafico-flex-container">
                  <div className="grafico-pie-container">
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={ingresosPorHabitacion}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="ingresos"
                        >
                          {ingresosPorHabitacion.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="grafico-tabla-container">
                    <table className="grafico-leyenda">
                      <thead>
                        <tr>
                          <th>Habitación</th>
                          <th>Ingresos</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ingresosPorHabitacion.map((entry, index) => (
                          <tr key={`legend-${index}`}>
                            <td>
                              <span className="color-dot" style={{ backgroundColor: COLORS[index % COLORS.length] }}></span>
                              {entry.name}
                            </td>
                            <td>{formatearMoneda(entry.ingresos)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="mensaje-sin-datos">No hay datos de ingresos por habitación para mostrar</div>
              )}
            </div>
          </div>

          {/* Gráfico de ocupación por habitación */}
          <div className="dashboard-card">
            <div className="card-header">
              <h2>Ocupación por Habitación {selectedMonth !== null ? `- ${meses[selectedMonth]}` : ''} {selectedYear}</h2>
            </div>
            <div className="card-content">
              {ocupacionPorHabitacion.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={ocupacionPorHabitacion}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="id" 
                      angle={0} 
                      textAnchor="middle"
                      height={50}
                      label={{ 
                        value: 'Habitaciones', 
                        position: 'insideBottomCenter',
                        dy: 15
                      }}
                    />
                    <YAxis 
                      label={{ 
                        value: 'Ocupación (%)', 
                        angle: -90, 
                        position: 'insideLeft' 
                      }}
                      domain={[0, 'auto']}
                      allowDecimals={false}
                    />
                    <Tooltip 
                      formatter={(value) => `${value}%`}
                      labelFormatter={(id) => {
                        const habitacion = ocupacionPorHabitacion.find(h => h.id == id);
                        return habitacion ? habitacion.nombre : `Habitación ${id}`;
                      }}
                    />
                    <Bar 
                      dataKey="ocupacion" 
                      fill="#82ca9d" 
                      name="Ocupación"
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="mensaje-sin-datos">No hay datos de ocupación para mostrar</div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Menú flotante */}
      <FloatingMenu />
    </div>
  );
};

export default AdminStatistics; 